import { Fragment, useEffect } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { Stack } from "@/Components/v2/atoms/Stack";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Title from "@/shared/components/Sections/Title";
import { Layer } from "@/Utils/hierarchy";

import { useBrandEquity } from "./api/useBrandEquity";
import BrandEquityChart from "./components/Charts/BrandEquityChart";
import { Header } from "./components/Sections/Header";
import { content } from "./constants/content";
import { useBrandEquityStore } from "./store/useBrandEquityStore";

interface BrandEquityProps {}

export const BrandEquity: React.FC<BrandEquityProps> = () => {
  const { nodes, loading } = useInfo();
  const { startDate, endDate, minDate, maxDate } = useDateRangeStore();

  const { targetNodeUUID } = useBrandEquityStore((state) => state.filters);
  const { setTargetNode, chart } = useBrandEquityStore();

  // Set default node
  useEffect(() => {
    if (nodes && !loading && targetNodeUUID === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;

      if (defaultNodeUUID !== undefined) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [loading, targetNodeUUID, nodes]);

  const selectedNode = nodes?.getNode(targetNodeUUID || "");

  const { moduleOpened } = useAnalytics();

  const {
    data: brandEquityData,
    isLoading: brandEquityLoading,
    error,
  } = useBrandEquity({
    brand: selectedNode?.node.brand || "",
    category: selectedNode?.node.category || "",
    product: selectedNode?.node.product || "",
    startDate,
    endDate,
    dateGranularity: chart.dateGranularity,
    config: {
      enabled: !!selectedNode,
    },
  });

  useEffect(() => {
    moduleOpened("brand-equity", Layer.Portfolio);
  }, []);

  if (!nodes || loading || !targetNodeUUID) {
    return <CircularProgress size={150} sx={{ marginY: "30rem" }} />;
  }

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header
        nodes={nodes}
        targetNode={targetNodeUUID}
        setTargetNode={setTargetNode}
        minDate={minDate}
        maxDate={maxDate}
      />
      {brandEquityLoading || !selectedNode ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : (
        <Fragment>
          <BrandEquityChart
            monthAllResponse={brandEquityData as any}
            monthErrors={error as any}
            weekAllResponse={brandEquityData as any}
            weekErrors={error as any}
          />
        </Fragment>
      )}
    </Box>
  );
};

export default BrandEquity;
